import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { isNull } from 'util';
import moment from 'moment';

import Input from 'Components/Input';
import useReducedErrors from 'Shared/hooks/useReducedErrors';
import LogoFirst from '../../../Colleagues/assets/RandomCat1.png';
import LogoSecond from '../../../Colleagues/assets/RandomCat2.png';
import Tag from 'Components/Tag/Tag';
import copyToClipboard from 'Shared/util/copyToClipboard';
import ToggleButton from 'Components/ToggleButton/ToggleButton';
import useNightMode from 'Shared/hooks/useNightMode';

const transitionMode = () => {
  document.documentElement.classList.add('transition');
  window.setTimeout(() => {
    document.documentElement.classList.remove('transition');
  }, 1000);
};

const TabPersonal = (props) => {
  const httpsPrefixLinkedIn = 'https://www.linkedin.com/in/';
  const httpsPrefixGitHub = 'https://github.com/';

  const {
    formik: {
      values: { email, firstName, lastName, phone, birthDate, github, linkedin, telegram, avatar },
      errors,
      handleBlur,
      handleChange,
      setValues,
      setFieldValue,
      validateForm,
      submitForm,
    },
    data,
  } = props;

  useMemo(() => {
    if (data && data.user.employeeprofile) {
      setValues(
        (({
          user: {
            email,
            firstName,
            lastName,
            employeeprofile: { phone, birthDate, github, linkedin, telegram, avatar },
          },
        }) => ({
          email,
          firstName,
          lastName,
          phone,
          birthDate: moment(birthDate).format('DD/MM/YYYY'),
          github: github || '',
          linkedin: linkedin || '',
          telegram: isNull(telegram) ? '' : telegram,
          avatar: !avatar ? LogoFirst : avatar,
        }))(data)
      );
    }
  }, [data, setValues]);

  useEffect(() => {
    //cache data until user closes Profile tab
    const localStoragePersonalData = JSON.parse(localStorage.getItem('personalData'));
    if (localStoragePersonalData) {
      setValues(localStoragePersonalData);
    }
  }, [data, setValues]);

  useEffect(() => {
    localStorage.setItem(
      'personalData',
      JSON.stringify({
        email,
        firstName,
        lastName,
        phone,
        birthDate,
        github,
        linkedin,
        telegram,
        avatar,
      })
    );
  }, [avatar, birthDate, email, firstName, lastName, phone, github, linkedin, telegram]);

  const [localPhone, setLocalPhone] = useState(phone);
  const [localGithub, setLocalGithub] = useState(github);
  const [localLinkedin, setLocalLinkedin] = useState(linkedin);
  const [nightMode, updateNightMode] = useNightMode();

  const onChangeColorMode = () => {
    const status = nightMode;
    localStorage.setItem('nightMode', !status);

    if (!status) {
      transitionMode();
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      transitionMode();
      document.documentElement.setAttribute('data-theme', 'light');
    }
  };

  useEffect(() => {
    validateForm();
    if (!localPhone && localPhone !== phone) {
      let value = phone;
      if (value && value.startsWith('+380')) {
        value = value.slice('+380'.length);
      }
      setLocalPhone(value);
    }
  }, [localPhone, phone, setLocalPhone, validateForm]);

  useEffect(() => {
    validateForm();
    if (!localGithub && localGithub !== github) {
      let value = github;
      if (value && value.startsWith(httpsPrefixGitHub)) {
        value = value.slice(httpsPrefixGitHub.length);
      }
      setLocalGithub(value);
    }
  }, [github, localGithub, setLocalGithub, validateForm]);

  useEffect(() => {
    validateForm();
    if (!localLinkedin && localLinkedin !== linkedin) {
      let value = linkedin;
      if (value && value.startsWith(httpsPrefixLinkedIn)) {
        value = value.slice(httpsPrefixLinkedIn.length);
      }
      setLocalLinkedin(value);
    }
  }, [linkedin, localLinkedin, setLocalLinkedin, validateForm]);

  const reducedErrors = useReducedErrors(errors);

  const hiddenInputRef = useRef(null);

  const parseAvatar = useCallback(() => {
    if (avatar && !reducedErrors.avatar) {
      return { backgroundImage: `url(${avatar})` };
    }
    return { backgroundImage: data && data.user && data.user.id % 2 ? LogoFirst : LogoSecond };
  }, [avatar, data, reducedErrors]);

  const handleAvatarFileSelection = useCallback(
    (e) => {
      const file = e.target.files[0];

      if (!file) {
        return;
      }

      const reader = new FileReader();

      reader.onloadend = () => {
        if (reader.result && reader.result.includes('image')) {
          setFieldValue('avatar', reader.result);
          submitForm();
        }
      };

      reader.readAsDataURL(file);
    },
    [setFieldValue, submitForm]
  );

  return (
    <div className="profile-tab" data-testid="profile-personal">
      <div className="profile-tab__section">
        <div className="profile-avatar" style={parseAvatar()}>
          <input type="file" className="profile-avatar__input" onChange={handleAvatarFileSelection} />
          <input
            type="button"
            className="profile-avatar__input-button"
            value=""
            onClick={() => [...document.getElementsByClassName('profile-avatar__input')][0].click()}
          />
        </div>
        <span className="profile-tab__fake-label">Your email address</span>
        <div className="profile-tab__fake-input">
          <div className="profile-tab__email">
            {email}
            <Tag className="email-copy-tag" size="small" variant="gray" text="Copied" />
          </div>
          <input type="text" readOnly className={'profile-tab__email-field'} ref={hiddenInputRef} />
          <span
            className={'profile-tab__btn-copy'}
            onClick={() => {
              copyToClipboard(email);

              let emailTagClassList = document.getElementsByClassName('email-copy-tag')[0].classList;
              emailTagClassList.add('email-copy-tag_active');
              setTimeout(() => {
                emailTagClassList.remove('email-copy-tag_active');
              }, 2000);
            }}
          >
            Copy
          </span>
        </div>
        <Input
          label="First name"
          helperVariant={reducedErrors['firstName'] ? 'error' : 'message'}
          helper={reducedErrors['firstName']}
          fieldProps={{
            placeholder: 'Enter first name',
            name: 'firstName',
            value: firstName,
            onChange: handleChange,
            onBlur: handleBlur,
          }}
        />
        <Input
          label="Last name"
          helperVariant={reducedErrors['lastName'] ? 'error' : 'message'}
          helper={reducedErrors['lastName']}
          fieldProps={{
            placeholder: 'Enter last name',
            name: 'lastName',
            value: lastName,
            onChange: handleChange,
            onBlur: handleBlur,
          }}
        />
        <Input
          label="Phone"
          prefix="+380"
          helperVariant={reducedErrors['phone'] ? 'error' : 'message'}
          helper={reducedErrors['phone']}
          inputMask={'99 999 99 99'}
          maskChar={'_'}
          fieldProps={{
            placeholder: '(XX) XXX XX XX',
            name: 'phone',
            value: localPhone,
            onChange: ({ target: { value } }) => setLocalPhone(value),
            onBlur: () => {
              setFieldValue('phone', '+380' + localPhone.split(' ').join(''), true);
            },
          }}
        />
        <Input
          label="Telegram"
          helperVariant={reducedErrors['telegram'] ? 'error' : 'message'}
          prefix="@"
          helper={reducedErrors['telegram']}
          fieldProps={{
            placeholder: 'username',
            name: 'telegram',
            value: telegram,
            onChange: ({ target: { value } }) => setFieldValue('telegram', value.replace('@', '')),
            onBlur: handleBlur,
          }}
        />
        <Input
          label="Birth date"
          helperVariant={reducedErrors['birthDate'] ? 'error' : 'message'}
          helper={reducedErrors['birthDate']}
          inputMask={'99/99/9999'}
          maskChar={'_'}
          fieldProps={{
            placeholder: 'DD/MM/YYYY',
            name: 'birthDate',
            value: birthDate,
            onChange: handleChange,
            onBlur: handleBlur,
          }}
        />
      </div>
      <Input
        label="GitHub"
        prefix="https://github.com/"
        helperVariant={reducedErrors['github'] ? 'error' : 'message'}
        helper={reducedErrors['github']}
        fieldProps={{
          placeholder: 'your_profile',
          name: 'github',
          value: localGithub,
          onChange: ({ target: { value } }) => setLocalGithub(value),
          onBlur: () => {
            setFieldValue('github', localGithub ? httpsPrefixGitHub + localGithub : '', true);
          },
        }}
      />
      <Input
        label="LinkedIn"
        prefix="https://www.linkedin.com/in/"
        helperVariant={reducedErrors['linkedin'] ? 'error' : 'message'}
        helper={reducedErrors['linkedin']}
        fieldProps={{
          name: 'linkedin',
          placeholder: 'your_profile',
          value: localLinkedin,
          onChange: ({ target: { value } }) => setLocalLinkedin(value),
          onBlur: () => {
            setFieldValue('linkedin', localLinkedin ? httpsPrefixLinkedIn + localLinkedin : '', true);
          },
        }}
      />
      <div className="profile-tab__night-mode">
        <ToggleButton
          extractToggle={true}
          onToggleChange={() => {
            onChangeColorMode();
            updateNightMode(!nightMode);
          }}
          toggleStatus={nightMode}
          inversion={true}
          id={'night-mode'}
        />
        <p className="night-mode__label">Night mode</p>
      </div>
    </div>
  );
};

export default TabPersonal;
